import { gsap } from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

(function () {

  // JS Loaded
  document.body.classList.add("js-loaded");

  // -----------------------
  // GSAP setup (safe guards)
  // -----------------------
  const hasGSAP = typeof gsap !== "undefined";

  if (hasGSAP) {
    // Register plugins that are bundled/imported with the app
    const plugins = [ScrollTrigger, ScrollSmoother, SplitText];
    if (window.CustomEase) plugins.push(window.CustomEase);

    gsap.registerPlugin(...plugins);

    // Custom ease (only if CustomEase exists)
    if (window.CustomEase) {
      try {
        CustomEase.create("smoothEase", "M0,0 C0.6,0 0.14,0.98 1,1");
      } catch (e) {
        // ignore if already created
      }
    }
  }

  // Register GSAP plugins
    gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

    // -----------------------
    // Smoother
    // -----------------------
    // ScrollSmoother wraps the whole page in a transform and runs work every
    // frame. On phones/tablets it hurts scroll performance and INP for little
    // visual gain, so only enable it on larger, pointer-capable screens and
    // when the user hasn't asked for reduced motion.
    const prefersReducedMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canUseSmoothScroll =
      window.matchMedia && window.matchMedia("(min-width: 1025px)").matches;

    let smoother = null;
    if (canUseSmoothScroll && !prefersReducedMotion) {
      smoother = ScrollSmoother.create({
        smooth: 2,
        effects: true,
      });
    }



      // =========================
    // Header Animation
    // =========================
    const initShowHideHeader = () => {
        const header = document.querySelector('#header');
        if (!header) return;
        const isHome = document.body.classList.contains('home') || document.body.classList.contains('front-page');
        const bgClasses = ["bg-primary", "bg-foreground", "bg-trans", "bg-background"];
        const textClasses = ["text-foreground", "text-background"];
        const initialBg = bgClasses.find((c) => header.classList.contains(c));
        const initialText = textClasses.find((c) => header.classList.contains(c));
        const applyHeaderTheme = (mode) => {
            bgClasses.forEach((c) => header.classList.remove(c));
            textClasses.forEach((c) => header.classList.remove(c));
            if (mode === "solid") {
                header.classList.add("bg-foreground", "text-background");
                return;
            }
            if (initialBg) header.classList.add(initialBg);
            if (initialText) header.classList.add(initialText);
        };

        let isHeaderVisible = true;
        const showHeaderAnim = gsap.from(header, {
            yPercent: -100,
            paused: true,
            duration: 0.3,
            onComplete: () => {
                isHeaderVisible = true;
            },
            onReverseComplete: () => {
                isHeaderVisible = false;
            }
        }).progress(1);

        if (isHome) {
            ScrollTrigger.create({
                id: "header-at-top",
                trigger: document.body,
                start: "top top",
                end: "+=120",
                onEnter: () => applyHeaderTheme("initial"),
                onEnterBack: () => applyHeaderTheme("initial")
            });
        }

        ScrollTrigger.create({
            id: "header-visibility",
            trigger: "#header",
            start: `bottom top`,
            // markers: true,
            end: 99999,
            onUpdate: (self) => {
                self.direction === -1 ? showHeaderAnim.play() : showHeaderAnim.reverse();
                if (!isHome) return;
                if (self.scroll() <= 10) {
                    applyHeaderTheme("initial");
                    return;
                }
                if (self.direction === -1) applyHeaderTheme("solid");
                else if (isHeaderVisible) applyHeaderTheme("solid");
            }
        });

    }
    initShowHideHeader();


      // -----------------------
    // Header Animation
    // -----------------------
    // const header = document.querySelector("[data-animate-header]");
    // gsap.from(header, {
    //   yPercent: -100,
    //   duration: 1,
    //   ease: "power4.out",
    //   delay: 0.4,
    //   scrollTrigger: {
    //     trigger: header,
    //     start: "top top",
    //     once: true
    //   }
    // });


  // -----------------------
  // Back To Top  
  // -----------------------
  const backToTopBtn = document.querySelector("[data-backtotop]");
  if (backToTopBtn) {
    backToTopBtn.addEventListener("click", (e) => {
      e.preventDefault();
      const canUseSmoother = smoother && typeof smoother.enabled === "function" && smoother.enabled();
      if (canUseSmoother) smoother.scrollTo(0, true);
      else window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  // -----------------------
  // Flair Button — Slide Up  
  // -----------------------
  const canHover = window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  window.initFlairButton = (btn) => {
    if (!hasGSAP) return;
    if (!btn || btn.__flairInit) return;
    btn.__flairInit = true;

    const flair = btn.querySelector("[data-flair-bg]");
    const wrapper = btn.querySelector("[data-flair-text]");
    if (!flair || !wrapper) return;

    if (!canHover) {
      // Keep the hover layer hidden on touch devices without wiring events.
      gsap.set(flair, { yPercent: 100 });
      return;
    }

    const [t1, t2, t3] = wrapper.children;
    if (!t1 || !t2 || !t3) return;

    // initial
    gsap.set(flair, { yPercent: 100 });

    gsap.set(t1, { yPercent: 0, clipPath: "inset(0% 0% 0% 0)" });
    gsap.set([t2, t3], { yPercent: 0, clipPath: "inset(100% 0% 0% 0)" });

    btn.addEventListener("mouseenter", () => {
      gsap.killTweensOf([flair, t1, t2, t3]);

      gsap.set(flair, { yPercent: 100 });
      gsap.to(flair, {
        yPercent: 0,
        duration: 0.5,
        ease: "smoothEase",
      });

      gsap.to([t1, t2, t3], {
        yPercent: -100,
        duration: 0.5,
        ease: "expo.out",
      });

      gsap.to(t1, {
        clipPath: "inset(100% 0% 0% 0)",
        duration: 0.5,
        ease: "smoothEase",
      });

      gsap.to(t2, {
        clipPath: "inset(0% 0% 0% 0)",
        duration: 0.5,
        ease: "smoothEase",
      });

      gsap.set(t3, { clipPath: "inset(100% 0% 0% 0)" });
    });

    btn.addEventListener("mouseleave", () => {
      gsap.killTweensOf([flair, t1, t2, t3]);

      gsap.to(flair, {
        yPercent: -100,
        duration: 0.5,
        ease: "smoothEase",
      });

      gsap.to([t1, t2, t3], {
        yPercent: -200,
        duration: 0.5,
        ease: "smoothEase",
        onComplete() {
          if (!btn.matches(":hover")) {
            gsap.set(flair, { yPercent: 100 });
            gsap.set(t1, { yPercent: 0, clipPath: "inset(0% 0% 0% 0)" });
            gsap.set([t2, t3], { yPercent: 0, clipPath: "inset(100% 0% 0% 0)" });
          }
        },
      });

      gsap.to(t2, {
        clipPath: "inset(100% 0% 0% 0)",
        duration: 0.5,
        ease: "smoothEase",
      });

      gsap.to(t3, {
        clipPath: "inset(0% 0% 0% 0)",
        duration: 0.5,
        ease: "smoothEase",
      });
    });
  };

  // init flair buttons in static DOM
  if (hasGSAP) {
    document.querySelectorAll("[data-flair-btn]").forEach((btn) => window.initFlairButton(btn));
  }

  // -----------------------
  // SplitText + Fade animations per section
  // -----------------------
  if (hasGSAP && ScrollTrigger && SplitText && document.fonts?.ready) {
    document.fonts.ready.then(() => {
      gsap.utils.toArray("section").forEach((section) => {
        const items = section.querySelectorAll(
          "[data-animate-heading], [data-animate-paragraph], [data-animate-paragraphs], [data-animate-zoom-in], [data-animate-fade-up], [data-animate-fade-in], [data-animate-line]"
        );
        if (!items.length) return;

      // Build the timeline (and run the heavy SplitText DOM work) lazily, only
      // when the section is about to enter the viewport. Splitting every
      // heading/paragraph on the page up-front blocks the main thread on load.
      const buildSectionTimeline = () => {
        const tl = gsap.timeline();

        tl.call(() => {
          items.forEach((n) => (n.style.visibility = "visible"));
        }, null, 0);

        items.forEach((el, index) => {
          const position = index === 0 ? 0 : "<+=0.2";

          if (el.hasAttribute("data-animate-heading")) {
            const split = SplitText.create(el, {
              type: "lines, words",
              linesClass: "line",
              mask: "words",
            });

            tl.fromTo(
              split.lines,
              {
                clipPath: "inset(0% 0% 100% 0%)",
                opacity: 0,
                yPercent: 200,
                scaleX: 0.95,
                rotateX: 60,
              },
              {
                clipPath: "inset(0% 0% 0% 0%)",
                opacity: 1,
                yPercent: 0,
                scaleX: 1,
                duration: 1.2,
                stagger: 0.1,
                rotateX: 0,
                transformOrigin: "50% 100%",
                ease: "expo.out",
                delay: index === 0 ? 0.2 : 0,
              },
              position
            );
          } else if (el.hasAttribute("data-animate-paragraph")) {
            const split = SplitText.create(el, {
              type: "lines, words",
              linesClass: "line",
              mask: "words",
            });

            tl.from(
              split.lines,
              {
                duration: 1.0,
                yPercent: 100,
                opacity: 0,
                clipPath: "inset(0% 0% 100% 0%)",
                transformOrigin: "50% 100%",
                stagger: 0.08,
                ease: "expo.out",
              },
              position
            );
          } 
          // 2) Paragraphs (multiple <p> children)
      else if (el.hasAttribute("data-animate-paragraphs")) {
        const blocks = el.querySelectorAll("p, ul, ol");
        const targets = blocks.length ? blocks : [el];

        targets.forEach((target, idx) => {
          const splitTarget = target;

          if (target.tagName === "UL" || target.tagName === "OL") {
            const startAt = idx === 0 ? position : "<+=0.2";
            tl.from(
              target,
              {
                duration: 0.8,
                y: 20,
                opacity: 0,
                ease: "power2.out",
              },
              startAt
            );
            return;
          }

          const split = SplitText.create(splitTarget, {
            type: "lines, words",
            linesClass: "line",
            mask: "words",
          });

          const startAt = idx === 0 ? position : "<+=0.2";

          tl.from(
            split.lines,
            {
              duration: 1.0,
              yPercent: 100,
              opacity: 0,
              clipPath: "inset(0% 0% 100% 0%)",
              transformOrigin: "50% 100%",
              stagger: 0.08,
              ease: "expo.out",
            },
            startAt
          );
        });
      }
          
          else if (el.hasAttribute("data-animate-zoom-in")) {
            tl.from(
              el,
              {
                opacity: 0,
                scale: 1.2,
                duration: 1.0,
                ease: "power2.out",
              },
              position
            );
          } else if (el.hasAttribute("data-animate-fade-up")) {
            tl.from(
              el,
              {
                y: 40,
                opacity: 0,
                scale: 0.98,
                willChange: "transform,opacity,filter",
                duration: 1.0,
                ease: "power3.out",
                clearProps: "transform,opacity,filter,willChange",
              },
              position
            );
          } else if (el.hasAttribute("data-animate-fade-in")) {
            tl.from(
              el,
              {
                opacity: 0,
                willChange: "transform,opacity,filter",
                duration: 1.0,
                ease: "power3.out",
                clearProps: "transform,opacity,filter,willChange",
              },
              position
            );
          } else if (el.hasAttribute("data-animate-line")) {
            gsap.set(el, { transformOrigin: "left center" });
            tl.from(
              el,
              {
                scaleX: 0,
                duration: 1.0,
                ease: "power2.out",
              },
              index === 0 ? 0 : "<-=0.25"
            );
          }
        });
      };

      ScrollTrigger.create({
        trigger: section,
        start: "top 75%",
        once: true,
        onEnter: buildSectionTimeline,
      });
      });

      ScrollTrigger.refresh();
    });
  }

  // -----------------------
  // Swiper: Info slider + pager
  // -----------------------
  const infoEl = document.querySelector(".infoSwiper");
  const progressBar = document.querySelector("[data-progress-bar]");

  if (infoEl && typeof window.Swiper !== "undefined") {
    function updatePager(swiper) {
      if (!progressBar) return;

      const totalPages = swiper.snapGrid?.length || 1;  
      const pageIndex = swiper.snapIndex ?? 0;

      const widthPct = totalPages <= 1 ? 100 : 100 / totalPages;
      progressBar.style.width = `${widthPct}%`;

      progressBar.style.left = `${pageIndex * widthPct}%`;
    }

    const infoSwiper = new Swiper(".infoSwiper", {
      speed: 400,
      spaceBetween: 20,
      loop: false,

      slidesPerView: 1,
      slidesPerGroup: 1,

      navigation: {
        nextEl: ".js-info-next",
        prevEl: ".js-info-prev",
      },

      breakpoints: {
        0: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 16 },
        1024: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 20 },
        1280: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 20 },
      },

      on: {
        init(swiper) {
          updatePager(swiper);
        },
        slideChange(swiper) {
          updatePager(swiper);
        },
        breakpoint(swiper) {
          updatePager(swiper);
        },
        resize(swiper) {
          updatePager(swiper);
        },
      },
    });
  }

  // -----------------------
  // Swiper: Related slider 
  // -----------------------
  const relatedEl = document.querySelector(".relatedSwiper");

  if (relatedEl && typeof window.Swiper !== "undefined") {

    const relatedEl = new Swiper(".relatedSwiper", {
      speed: 400,
      spaceBetween: 20,
      loop: false,

      slidesPerView: 1,
      slidesPerGroup: 1,

      navigation: {
        nextEl: ".js-related-next",
        prevEl: ".js-related-prev",
      },

      breakpoints: {
        0: { slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 16 },
        1024: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 20 },
        1280: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 20 },
      },
    });
  }

  // -----------------------
  // Testimonials: Info slider + pager
  // -----------------------

  const testimonialsEl = document.querySelector("[data-testimonials-swiper]");
  if (testimonialsEl && typeof window.Swiper !== "undefined") {

  new Swiper(testimonialsEl, {
    speed: 600,
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    autoHeight: true,

    navigation: {
      nextEl: ".js-testimonials-next",
      prevEl: ".js-testimonials-prev",
    },

    pagination: {
      el: ".js-testimonials-pagination",
      // clickable: true,
    },
  });
 }
  

  // -----------------------
  // Logo marquee (GSAP)
  // -----------------------
  function initLogoMarquee(root) {
    if (!root || !hasGSAP) return;

    const track = root.querySelector("[data-track]");
    const row = root.querySelector("[data-row]");
    const clone = root.querySelector("[data-row-clone]");
    if (!track || !row || !clone) return;

    // clone once
    clone.innerHTML = row.innerHTML;

    let tween;
    let isVisible = true;

    const build = () => {
      tween?.kill();
      gsap.set(track, { x: 0 });

      const distance = row.scrollWidth;
      const pxPerSecond = 120;  
      const duration = distance / pxPerSecond;

      tween = gsap.to(track, {
        x: -distance,
        duration,
        ease: "none",
        repeat: -1,
        paused: !isVisible,
      });
    };

    build();

    const ro = new ResizeObserver(build);
    ro.observe(row);

    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (!tween) return;
        isVisible ? tween.resume() : tween.pause();
      },
      { threshold: 0.15 }
    );
    io.observe(root);

    root.addEventListener("mouseenter", () => tween?.pause());
    root.addEventListener("mouseleave", () => isVisible && tween?.resume());
  }

  document.querySelectorAll("[data-logo-marquee]").forEach(initLogoMarquee);

    // -----------------------
  // Count-Up animation for stats (optional)
  // -----------------------
  if (hasGSAP && ScrollTrigger) {
    gsap.utils.toArray("[data-animate-counter]").forEach((el) => {
      const rawValue = el.getAttribute("data-animate-counter") || "";
      const normalizedValue = rawValue.replace(/,/g, "");
      const endValue = parseFloat(normalizedValue);
      if (isNaN(endValue)) return;

      const decimalMatch = normalizedValue.split(".");
      const decimals = decimalMatch.length > 1 ? decimalMatch[1].length : 0;
      const formatter = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      const snapStep = decimals ? 1 / Math.pow(10, decimals) : 1;

      gsap.fromTo(
        el,
        { innerText: 0 },
        {
          innerText: endValue,
          duration: 1.8,
          ease: "power1.out",
          snap: { innerText: snapStep },
          scrollTrigger: {
            trigger: el.closest("section") || el,
            start: "top center",
            once: true,
          },
          onUpdate: function () {
            const value = Number(el.innerText);
            if (isNaN(value)) return;
            el.innerText = formatter.format(value);
          },
        }
      );
    });
  }

  // -----------------------
// Timeline (VERTICAL DOT) — pin + scrub steps
// -----------------------
function initTimelineVertical() {
  const section = document.querySelector("[data-timeline-section]") || document.querySelector("[data-timeline]")?.closest("section");
  if (!section || typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;

  const timeline = section.querySelector("[data-timeline]");
  const dot = section.querySelector("[data-timeline-dot]");
  const yearEls = Array.from(section.querySelectorAll("[data-timeline-year]"));

  
  const entries = Array.from(section.querySelectorAll("[data-timeline-entry]"));
  const entriesWrapper = section.querySelector("[data-timeline-entries]");

  if (!timeline || !dot || yearEls.length === 0) return;

  // ----------------------
  // Dot Y positions (center of each year label)
  // ----------------------
  const getPositions = () => {
    const barRect = timeline.getBoundingClientRect();
    return yearEls.map((year) => {
      const r = year.getBoundingClientRect();
      // y position inside timeline box (relative)
      return r.top + r.height / 2 - barRect.top;
    });
  };

  let positions = getPositions();

  const moveDot = gsap.quickTo(dot, "y", {
    duration: 0.6,
    ease: "power2.out",
  });

  // ----------------------
  // Active state
  // ----------------------
  let currentIndex = 0;

  const setActive = (index) => {
    if (index === currentIndex) return;
    currentIndex = index;

    yearEls.forEach((el, i) => el.classList.toggle("is-active", i === index));

    // If you still use entries (old version), fade between them
    if (entries.length) {
      entries.forEach((entry, i) => {
        gsap.to(entry, {
          autoAlpha: i === index ? 1 : 0,
          duration: 0.6,
          ease: "power2.out",
          overwrite: "auto",
        });
      });
    }

    moveDot(positions[index]);
  };

  // initial
  yearEls.forEach((el, i) => el.classList.toggle("is-active", i === 0));
  moveDot(positions[0]);

  // old version: initial visibility + auto wrapper height
  const resizeWrapper = () => {
    if (!entriesWrapper || !entries.length) return;

    let maxH = 0;
    entries.forEach((entry) => {
      const prevPos = entry.style.position;
      const prevTop = entry.style.top;
      const prevLeft = entry.style.left;
      const prevRight = entry.style.right;
      const prevBottom = entry.style.bottom;

      entry.style.position = "static";
      entry.style.top = entry.style.left = entry.style.right = entry.style.bottom = "";

      const h = entry.offsetHeight;
      if (h > maxH) maxH = h;

      entry.style.position = prevPos || "absolute";
      entry.style.top = prevTop || "0";
      entry.style.left = prevLeft || "0";
      entry.style.right = prevRight || "0";
      entry.style.bottom = prevBottom || "0";
    });

    entriesWrapper.style.height = maxH + "px";
  };

  if (entries.length) {
    entries.forEach((entry, i) => gsap.set(entry, { autoAlpha: i === 0 ? 1 : 0 }));
    resizeWrapper();
  }

  // ----------------------
  // Click-to-change steps
  // ----------------------
  yearEls.forEach((el, i) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      setActive(i);
    });
  });

  // Recalc on resize / load
  const recalc = () => {
    positions = getPositions();
    moveDot(positions[currentIndex]);
    resizeWrapper();
  };

  window.addEventListener("resize", recalc);

  window.addEventListener("load", () => {
    recalc();
  });
}
initTimelineVertical();


  // -----------------------
// Estimated reading time
// -----------------------
function estimatedReadingTime() {
  var targets = document.querySelectorAll('[estimated-reading-time]');
  if (!targets.length) {
    return;
  }
  var article = document.querySelector('article');
  if (!article) {
    return;
  }
  var text = article.textContent || '';
  text = text.trim();
  if (!text) {
    return;
  }
  var words = text.split(/\s+/).length;
  console.log('Reading time word count:', words);
  var minutes = Math.max(1, Math.ceil(words / 200));
  var label = minutes + ' Min' + (minutes > 1 ? 's' : '');
  for (var i = 0; i < targets.length; i++) {
    targets[i].textContent = label;
  }
}
estimatedReadingTime();



  // -----------------------
  // Listing Insights AJAX
  // -----------------------
  const initListingInsightsAjax = () => {
    if (!window.InsightsAjax) return;

    const sections = document.querySelectorAll("[data-insights-section]");
    if (!sections.length) return;

    const debounce = (fn, wait = 300) => {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    };

    const fetchSection = async (section, page = 1) => {
      const list = section.querySelector("[data-insights-list]");
      if (!list) return;

      const search = section.querySelector("[data-insights-search]")?.value || "";
      const category = section.querySelector("[data-insights-category]")?.value || "";
      const date = section.querySelector("[data-insights-date]")?.value || "";

      const payload = new FormData();
      payload.append("action", "listing_insights_filter");
      payload.append("nonce", InsightsAjax.nonce);
      payload.append("section", section.dataset.insightsSection || "");
      payload.append("search", search);
      payload.append("category", category);
      payload.append("date", date);
      payload.append("page", page);
      payload.append("base_url", window.location.href);

      try {
        const res = await fetch(InsightsAjax.ajaxUrl, {
          method: "POST",
          credentials: "same-origin",
          body: payload,
        });
        const json = await res.json();
        if (!json?.success) return;

        list.innerHTML = json.data.cards || "";

        const paginationWrap = section.querySelector("[data-insights-pagination-wrap]");
        if (json.data.pagination) {
          if (paginationWrap) {
            paginationWrap.outerHTML = json.data.pagination;
          } else {
            list.insertAdjacentHTML("afterend", json.data.pagination);
          }
        } else if (paginationWrap) {
          paginationWrap.remove();
        }

        if (hasGSAP) {
          list.querySelectorAll("[data-flair-btn]").forEach((btn) => window.initFlairButton(btn));
        }
      } catch (err) {
        console.error("Insights filter failed", err);
      }
    };

    sections.forEach((section) => {
      const form = section.querySelector("[data-insights-form]");
      if (form) {
        form.addEventListener("submit", (event) => {
          event.preventDefault();
          fetchSection(section, 1);
        });

        const debouncedSearch = debounce(() => fetchSection(section, 1), 400);

        form.addEventListener("input", (event) => {
          if (event.target.matches("[data-insights-search]")) {
            debouncedSearch();
          }
        });

        form.addEventListener("change", (event) => {
          if (event.target.matches("[data-insights-category], [data-insights-date]")) {
            fetchSection(section, 1);
          }
        });

      }

      section.addEventListener("click", (event) => {
        const link = event.target.closest("[data-insights-pagination] a");
        if (!link) return;
        event.preventDefault();

        const pageParam = section.dataset.pageParam || "page";
        const url = new URL(link.getAttribute("href"), window.location.href);
        const page = parseInt(url.searchParams.get(pageParam) || "1", 10);
        fetchSection(section, page);
      });
    });
  };

  initListingInsightsAjax();




})();
